import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PERKS = [
  "Toutes les histoires de la bibliothèque",
  "Lecture à voix haute avec mots surlignés",
  "10 histoires inventées et illustrées par l'IA chaque mois",
  "Atelier « Répète après moi » avec le micro",
  "« Raconte ! » : retours bienveillants pour mieux s'exprimer",
];

const PLANS = [
  { key: "premium_monthly", name: "Mensuel", price: "5,99 €", per: "TTC / mois", period: "chaque mois", amount: "5,99 € TTC", note: "Sans engagement, renouvelé chaque mois" },
  { key: "premium_yearly", name: "Annuel", price: "49 €", per: "TTC / an", period: "chaque année", amount: "49 € TTC", note: "Soit environ 4,08 € par mois, 2 mois offerts", best: true },
];

function PlanCard({ plan, selected, onSelect }) {
  return (
    <button type="button" data-testid={`plan-card-${plan.key}`} onClick={() => onSelect(plan.key)}
      className={`rise card-paper relative flex flex-col p-6 text-left transition-transform hover:-translate-y-1 ${selected ? "!border-[color:var(--terracotta)] ring-4 ring-[#F4A391]/40" : ""}`}>
      {plan.best && <span className="absolute -top-3 left-6 rounded-full bg-[color:var(--terracotta)] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white">Le plus choisi</span>}
      <h3 className="font-display text-2xl font-bold">{plan.name}</h3>
      <p className="mt-3"><span className="font-display text-5xl font-bold">{plan.price}</span> <span className="font-bold text-[color:var(--muted-ink)]">{plan.per}</span></p>
      <p className="mt-2 text-sm text-[color:var(--muted-ink)]">{plan.note}</p>
    </button>
  );
}

export default function Premium() {
  const { user } = useAuth();
  const [planKey, setPlanKey] = useState("premium_yearly");
  const [terms, setTerms] = useState(false);
  const [waiver, setWaiver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testMode, setTestMode] = useState(false);
  useEffect(() => { api.get("/payments/config").then((r) => setTestMode(r.data.test_mode)).catch(() => {}); }, []);
  const plan = PLANS.find((p) => p.key === planKey);
  const trial = user.trial_available;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: planKey, origin_url: window.location.origin, accept_terms: terms, withdrawal_waiver: waiver });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <main data-testid="premium-page" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="rise lg:col-span-5">
          <p className="inline-flex items-center gap-2 rounded-full bg-[color:var(--paper-2)] px-4 py-1.5 text-sm font-extrabold uppercase tracking-wider text-[color:var(--terracotta)]"><Crown size={16} /> Lis-Moi Premium</p>
          <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">Offrez à {user.child_name || "votre enfant"} toute la magie des mots.</h1>
          {trial && <p data-testid="premium-trial-badge" className="mt-4 inline-block rounded-full bg-[color:var(--sun)] px-4 py-1.5 font-display text-lg font-bold">7 jours gratuits pour essayer</p>}
          <ul className="mt-8 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-lg"><Check className="mt-1 shrink-0 text-[color:var(--sage)]" size={20} /> {p}</li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          {user.is_premium ? (
            <div data-testid="premium-active" className="card-paper p-8">
              <h2 className="font-display text-3xl font-bold">Vous êtes déjà Premium !</h2>
              <p className="mt-2 text-[color:var(--muted-ink)]">Accès actif jusqu'au {new Date(user.premium_until).toLocaleDateString("fr-FR")}. Gérez votre abonnement depuis <Link to="/profil" className="font-bold underline">Mes progrès</Link>.</p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2">
                {PLANS.map((p) => <PlanCard key={p.key} plan={p} selected={planKey === p.key} onSelect={setPlanKey} />)}
              </div>
              <div data-testid="premium-summary" className="card-paper mt-6 space-y-4 p-6">
                <p className="flex items-start gap-3 font-semibold">
                  <CalendarClock className="mt-0.5 shrink-0 text-[color:var(--terracotta)]" size={20} />
                  <span>
                    {trial
                      ? <>Aujourd'hui : <strong>0 €</strong>. Après 7 jours d'essai : <strong>{plan.amount}</strong> prélevés {plan.period === "chaque mois" ? "puis chaque mois" : "puis chaque année"}.</>
                      : <>Aujourd'hui : <strong>{plan.amount}</strong>, puis {plan.amount} {plan.period}.</>}
                    {" "}Renouvellement automatique jusqu'à annulation. Annulable à tout moment en un clic depuis Mes progrès{trial ? ", sans frais pendant l'essai" : ""}.
                  </span>
                </p>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input type="checkbox" data-testid="premium-terms-checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#E05A47]" />
                  <span>J'ai lu et j'accepte les <Link to="/cgu-cgv" target="_blank" className="font-bold underline">CGV</Link>, y compris le renouvellement automatique de l'abonnement.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input type="checkbox" data-testid="premium-waiver-checkbox" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#E05A47]" />
                  <span>Je demande l'accès immédiat au contenu Premium et je reconnais perdre mon droit de rétractation de 14 jours une fois l'exécution commencée{trial ? " (l'essai gratuit reste annulable sans frais)" : ""}.</span>
                </label>
                <button data-testid="premium-subscribe-button" disabled={busy || !terms || !waiver} onClick={subscribe} className="btn-primary w-full !py-4 !text-xl">
                  {busy ? <Loader2 className="animate-spin" size={20} /> : <Crown size={20} />}
                  {trial ? "Commencer mes 7 jours gratuits" : `S'abonner : ${plan.amount} ${plan.per.replace("TTC ", "")}`}
                </button>
              </div>
            </>
          )}
          <p className="mt-4 text-sm text-[color:var(--muted-ink)]">Paiement sécurisé par Stripe. Lis-Moi ne voit jamais votre carte.{testMode && " Mode test : carte 4242 4242 4242 4242, date future, n'importe quel code."}</p>
        </div>
      </div>
    </main>
  );
}
